"use client";

import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { SPORTSBOOK_ROOT } from "@/lib/site";
import { spring } from "@/lib/motion";
import { navGroups } from "@/lib/content/nav";
import type { NavItem as Item } from "@/lib/content/types";
import { Icon, type IconName } from "@/components/ui/Icon";
import { LanguageSelector } from "./LanguageSelector";
import { useI18n } from "@/lib/i18n/I18nProvider";
import { Badge, ProgressBar, Tooltip } from "@/components/ui/primitives";
import { RemoteImage } from "@/components/ui/RemoteImage";
import { RailWidgetSkeleton } from "@/components/ui/states";
import { useContent } from "./ContentContext";
import { useIdentity } from "@/lib/api/session";
import { isLocalCollectionHref } from "@/lib/player-collections";
import { usePlayer } from "./PlayerContext";
import { useShell } from "./ShellContext";
import { tierLevelView, useTierProgress } from "@/lib/api/incentives";
import { useBankrolls } from "@/lib/api/transfers";
import { countdownTag, countdownTickMs } from "@/lib/smartico/countdown";
import { levelSourceFor } from "@/lib/smartico/config";
import { useSmarticoLevel, useSmarticoMission } from "@/lib/smartico/hooks";
import type { MissionView } from "@/lib/smartico/mission";
import {
  LEVELS_DEEP_LINK,
  MISSIONS_DEEP_LINK,
  openSmartico,
  useSmartico,
} from "@/lib/smartico/SmarticoProvider";

export function SideNav({
  onClose,
  onNavigate,
  casinoNav,
}: {
  onClose?: () => void;
  onNavigate?: () => void;
  casinoNav: Item[];
}) {
  const pathname = usePathname();
  const content = useContent();
  const player = usePlayer();
  const vertical = pathname.startsWith(SPORTSBOOK_ROOT) ? "sports" : "casino";
  const railSocials = content.socials.filter((s) => s.rail);
  const languageCount = useI18n().enabledLocales.length;
  const primary = content.nav.primary.filter((item) => player.enabled || !isLocalCollectionHref(item.href));
  const sectioned = content.flags.navGroupStyle === "Non-grouped";
  const groups = navGroups(content.nav, casinoNav);

  return (
    <div className="flex h-full w-64 shrink-0 flex-col border-r border-border bg-elevation-1">
      {/* Top: menu + vertical toggle */}
      <div className="flex h-20 shrink-0 items-center gap-[12px] border-b border-border px-[16px]">
        <button
          onClick={onClose}
          aria-label="Collapse navigation"
          className="grid h-[38px] w-[38px] shrink-0 place-items-center rounded-lg bg-elevation-3 text-fg transition-colors hover:bg-elevation-4"
        >
          <Icon name="menu" size={20} />
        </button>
        {content.flags.sportsEnabled ? (
          <VerticalToggle value={vertical} onNavigate={onNavigate} />
        ) : (
          <span className="flex h-[38px] flex-1 items-center gap-[4px] rounded-lg bg-elevation-3 px-[12px] text-[12px] font-bold leading-[1.6] text-fg">
            <Icon name="spade" size={18} />
            Casino
          </span>
        )}
      </div>

      <div className="scrollbar-none flex-1 overflow-y-auto">
        {content.flags.xpWidgetEnabled && <RankWidget />}
        {content.flags.missionWidgetEnabled && <MissionWidget />}

        <NavList label="Primary" items={primary} pathname={pathname} onNavigate={onNavigate} sectioned={sectioned} />

        {/* The operator's groups, the platform's categories drawn into the
            one that asked for them — as collapsible cards, or laid open as
            labelled sections (Feature flags → Sidebar group style). */}
        {sectioned ? (
          groups.map((group, i) => (
            <NavList
              key={`${group.label}-${i}`}
              label={group.label}
              heading={group.label}
              items={group.items}
              pathname={pathname}
              onNavigate={onNavigate}
              sectioned
            />
          ))
        ) : (
          <div className="flex flex-col items-start gap-[8px] border-b border-border px-[16px] py-[8px]">
            {groups.map((group, i) => (
              <NavGroup
                key={`${group.label}-${i}`}
                label={group.label}
                icon={group.icon}
                items={group.items}
                pathname={pathname}
                defaultOpen={group.open}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        )}

        <NavList
          label="More"
          items={content.nav.tail}
          pathname={pathname}
          onNavigate={onNavigate}
          bordered={false}
          sectioned={sectioned}
        />

        {/* The selector's own row, only when there is a language to switch
            to — an English-only site keeps just the socials row. */}
        {languageCount > 1 && (
          <div className="flex items-center justify-center border-t border-border px-[8px] py-[10px]">
            <LanguageSelector up />
          </div>
        )}

        <div className="flex h-[70px] items-center justify-center gap-[8px] border-t border-border px-[8px] py-[12px]">
          {railSocials.map((s) => (
            <a
              key={s.label}
              href={s.href}
              target="_blank"
              rel="noreferrer noopener"
              aria-label={s.label}
              className="grid h-[38px] w-[38px] place-items-center rounded-lg bg-elevation-2 text-fg-muted transition-colors duration-200 hover:bg-elevation-3 hover:text-fg"
            >
              <Icon name={s.icon} size={20} />
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

/* The rail collapsed to icons alone, the kit's narrow sidebar: the same
   items in the same order, labels moved into tooltips on the right. The
   widgets, group headers, vertical toggle, language selector and socials
   need words to mean anything, so they are not drawn — the menu button at
   the top brings the full rail back. Shown in place of nothing when
   Feature flags → "Collapsed menu shows icons" is on. */
/* Under the Combined rail layout's top bar the bar
   already holds the menu button and the primary links, so the rail drops its
   own header and starts with the groups (Capella's, after Betmode). */
export function CollapsedSideNav({
  onOpen,
  casinoNav,
  underBar = false,
  expanded = false,
}: {
  onOpen: () => void;
  casinoNav: Item[];
  underBar?: boolean;
  /* Under the bar only: widened by the bar's menu button, each icon with
     its label and each group under its name (Betmode's menu). */
  expanded?: boolean;
}) {
  const pathname = usePathname();
  const content = useContent();
  const player = usePlayer();
  const vertical = pathname.startsWith(SPORTSBOOK_ROOT) ? "sports" : "casino";
  const languageCount = useI18n().enabledLocales.length;
  const primary = content.nav.primary.filter((item) => player.enabled || !isLocalCollectionHref(item.href));
  const wide = underBar && expanded;

  return (
    <div
      className={cn(
        "flex h-full shrink-0 flex-col border-r border-border",
        underBar ? "w-full bg-canvas/95 backdrop-blur-[18px]" : "w-16 bg-elevation-1",
      )}
    >
      {!underBar && (
        <div className="flex h-20 shrink-0 items-center justify-center border-b border-border">
          <button
            onClick={onOpen}
            aria-label="Expand navigation"
            className="grid h-[38px] w-[38px] place-items-center rounded-lg bg-elevation-3 text-fg transition-colors hover:bg-elevation-4"
          >
            <Icon name="menu" size={20} />
          </button>
        </div>
      )}

      <div className="scrollbar-none flex-1 overflow-y-auto">
        {/* The casino/sports pair, stacked — the same two links the expanded
            rail's toggle is made of, the lit one following the URL. */}
        {content.flags.sportsEnabled && (
          <nav aria-label="Verticals" className="flex flex-col items-center gap-[4px] border-b border-border px-[8px] py-[8px]">
            {VERTICALS.map((v) => (
              <Tooltip key={v.key} content={v.label} side="right">
                <Link
                  href={v.href}
                  aria-current={vertical === v.key ? "page" : undefined}
                  aria-label={v.label}
                  className={cn(
                    "grid h-[40px] w-[40px] place-items-center rounded-lg transition-colors duration-200",
                    vertical === v.key
                      ? "bg-brand text-fg-on-primary"
                      : "bg-elevation-3 text-fg-subtle hover:bg-elevation-4 hover:text-fg",
                  )}
                >
                  <Icon name={v.icon} size={20} />
                </Link>
              </Tooltip>
            ))}
          </nav>
        )}

        {!underBar && <IconRailList label="Primary" items={primary} pathname={pathname} />}
        {underBar ? (
          /* Under the bar each group is its own list, so the widened rail can
             set the group's name over it. */
          navGroups(content.nav, casinoNav).map((group) => (
            <IconRailList
              key={group.label}
              label={group.label}
              items={group.items}
              pathname={pathname}
              expanded={wide}
              heading={group.label}
              brandActive
            />
          ))
        ) : (
          <IconRailList
            label="Groups"
            items={navGroups(content.nav, casinoNav).flatMap((group) => group.items)}
            pathname={pathname}
          />
        )}
        <IconRailList
          label="More"
          items={content.nav.tail}
          pathname={pathname}
          bordered={false}
          expanded={wide}
          brandActive={underBar}
        />

        {/* The language selector as a globe, in the same seat it has on the
            expanded rail: its own row at the bottom, only when there is a
            language to switch to. */}
        {languageCount > 1 && (
          <div className="flex justify-center border-t border-border px-[8px] py-[10px]">
            <LanguageSelector icon />
          </div>
        )}
      </div>
    </div>
  );
}

/* The Rail layout's menu: a narrow rail that never collapses, every item an
   icon over its label (Hugo Casino's). Signed out, it opens with the way in;
   signed in, with the account. */
export function LabelledRail({ casinoNav }: { casinoNav: Item[] }) {
  const pathname = usePathname();
  const content = useContent();
  const player = usePlayer();
  const { t } = useI18n();
  const { openSignIn } = useShell();
  const { isSignedIn } = useIdentity();
  const vertical = pathname.startsWith(SPORTSBOOK_ROOT) ? "sports" : "casino";
  const languageCount = useI18n().enabledLocales.length;
  const primary = content.nav.primary.filter((item) => player.enabled || !isLocalCollectionHref(item.href));
  const grouped = navGroups(content.nav, casinoNav).flatMap((group) => group.items);

  return (
    <div className="flex h-full w-[88px] shrink-0 flex-col border-r border-border bg-elevation-1">
      <div className="flex h-20 shrink-0 items-center justify-center border-b border-border px-[8px]">
        {isSignedIn ? (
          <Link
            href="/account"
            aria-current={pathname === "/account" ? "page" : undefined}
            className="flex w-full flex-col items-center gap-[4px] rounded-lg px-[4px] py-[8px] text-fg-subtle transition-colors hover:bg-elevation-3 hover:text-fg"
          >
            <Icon name="user" size={22} />
            <span className="text-[10px] font-bold leading-[1.2]">{t("topbar.myAccount")}</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => openSignIn()}
            className="flex w-full flex-col items-center gap-[4px] rounded-lg bg-brand px-[4px] py-[8px] text-fg-on-primary transition-colors hover:bg-brand-bold"
          >
            <Icon name="user-plus" size={22} />
            <span className="text-[10px] font-bold leading-[1.2]">{t("topbar.register")}</span>
          </button>
        )}
      </div>

      <div className="scrollbar-none flex-1 overflow-y-auto">
        {content.flags.sportsEnabled && (
          <LabelledRailList
            label="Verticals"
            items={VERTICALS.map((v) => ({ label: v.label, href: v.href, icon: v.icon }))}
            isActive={(item) => item.href === (vertical === "sports" ? SPORTSBOOK_ROOT : "/")}
          />
        )}
        <LabelledRailList label="Primary" items={primary} isActive={(item) => pathname === item.href} />
        <LabelledRailList label="Groups" items={grouped} isActive={(item) => pathname === item.href} />
        <LabelledRailList label="More" items={content.nav.tail} isActive={(item) => pathname === item.href} bordered={false} />

        {languageCount > 1 && (
          <div className="flex justify-center border-t border-border px-[8px] py-[10px]">
            <LanguageSelector icon />
          </div>
        )}
      </div>
    </div>
  );
}

function LabelledRailList({
  label,
  items,
  isActive,
  bordered = true,
}: {
  label: string;
  items: Pick<Item, "label" | "href" | "icon" | "iconUrl">[];
  isActive: (item: Pick<Item, "href">) => boolean;
  bordered?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label={label}
      className={cn("flex flex-col items-stretch gap-[2px] px-[6px] py-[8px]", bordered && "border-b border-border")}
    >
      {items.map((item) => {
        const active = isActive(item);
        return (
          <Link
            key={`${item.label}-${item.href}`}
            href={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex flex-col items-center gap-[4px] rounded-lg px-[4px] py-[8px] text-center transition-colors duration-200",
              active ? "bg-elevation-2 text-brand-bold" : "text-fg-subtle hover:bg-elevation-3 hover:text-fg",
            )}
          >
            {item.iconUrl ? (
              <span className="relative grid h-[22px] w-[22px] place-items-center overflow-hidden rounded-[5px] bg-elevation-3">
                <RemoteImage src={item.iconUrl} alt="" width={22} height={22} />
              </span>
            ) : (
              <Icon name={item.icon} size={22} />
            )}
            <span className="line-clamp-2 w-full break-words text-[10px] font-bold leading-[1.2]">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

function IconRailList({
  label,
  items,
  pathname,
  bordered = true,
  expanded = false,
  heading,
  brandActive = false,
}: {
  label: string;
  items: Item[];
  pathname: string;
  bordered?: boolean;
  /* The rail under the Combined rail bar, widened by the bar's menu
     button: each icon with its label beside it, no tooltips. */
  expanded?: boolean;
  /* Shown over the items while expanded: the group's name. */
  heading?: string;
  /* The lit item in the brand colour (the rail under the bar). */
  brandActive?: boolean;
}) {
  if (items.length === 0) return null;

  return (
    <nav
      aria-label={label}
      className={cn(
        "flex flex-col gap-[4px] px-[8px] py-[8px]",
        expanded ? "items-stretch" : "items-center",
        bordered && "border-b border-border",
      )}
    >
      {expanded && heading && (
        <span className="truncate whitespace-nowrap px-[12px] pb-[2px] pt-[4px] font-heading text-[11px] font-extrabold uppercase tracking-[0.16em] text-fg-subtle">
          {heading}
        </span>
      )}
      {items.map((item) => {
        const active = pathname === item.href;
        const link = (
          <Link
            key={`${item.label}-${item.href}`}
            href={item.href}
            aria-label={item.label}
            aria-current={active ? "page" : undefined}
            data-pill={expanded ? "" : undefined}
            className={cn(
              "relative transition-colors duration-200",
              expanded
                ? "flex h-[42px] w-full items-center gap-[12px] whitespace-nowrap rounded-lg px-[10px] text-[13px] font-bold"
                : "grid h-[40px] w-[40px] place-items-center rounded-lg",
              active
                ? brandActive
                  ? "bg-brand/10 text-brand"
                  : "bg-elevation-2 text-fg"
                : "text-fg-subtle",
              !active && "hover:bg-elevation-3 hover:text-fg",
            )}
          >
            {active && brandActive && (
              <span
                aria-hidden
                className="absolute bottom-[8px] left-0 top-[8px] w-[2px] rounded-r-[2px] bg-brand shadow-[0_0_12px_var(--color-brand)]"
              />
            )}
            {item.iconUrl ? (
              <span className="relative grid h-[20px] w-[20px] shrink-0 place-items-center overflow-hidden rounded-[5px] bg-elevation-3">
                <RemoteImage src={item.iconUrl} alt="" width={20} height={20} />
              </span>
            ) : (
              <Icon name={item.icon} size={20} className="shrink-0" />
            )}
            {expanded && <span className="min-w-0 truncate">{item.label}</span>}
          </Link>
        );

        return expanded ? (
          link
        ) : (
          <Tooltip key={`${item.label}-${item.href}`} content={item.label} side="right">
            {link}
          </Tooltip>
        );
      })}
    </nav>
  );
}

/* The two verticals are two places, so the toggle is a pair of links: which
   one is lit follows the URL rather than a click, and the back button works.
   Exported so the Tile layout's product nav can switch on the same pair. */
export const VERTICALS = [
  { key: "casino", label: "Casino", icon: "spade", href: "/" },
  { key: "sports", label: "Sports", icon: "target", href: SPORTSBOOK_ROOT },
] as const;

function VerticalToggle({
  value,
  onNavigate,
}: {
  value: "casino" | "sports";
  onNavigate?: () => void;
}) {
  return (
    <div className="relative flex h-[38px] flex-1 items-center gap-[4px] rounded-lg bg-elevation-3 p-[4px]">
      {VERTICALS.map((v) => (
        <Link
          key={v.key}
          href={v.href}
          onClick={onNavigate}
          aria-current={value === v.key ? "page" : undefined}
          className={cn(
            "relative z-10 flex h-[30px] flex-1 items-center justify-center gap-[4px] rounded-md px-[12px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
            value === v.key ? "text-fg-on-primary" : "text-fg-subtle",
          )}
        >
          <Icon name={v.icon} size={18} />
          {v.label}
        </Link>
      ))}
      <motion.span
        layout
        transition={spring}
        className="absolute inset-y-[4px] z-0 w-[calc(50%-6px)] rounded-md bg-brand"
        style={{ left: value === "casino" ? 4 : "calc(50% + 2px)" }}
      />
    </div>
  );
}

/* The two widgets at the top of the rail, and where their data comes from.

   The kit draws both against Smartico: a level bar between two level crests,
   and one timed mission. They part company when Smartico is not there.

   The level bar has a second source, and which one it draws is the operator's
   stated choice — the whitelabel's `levelComponent.dataSource`, here the
   "Level widget shows" setting under /admin -> Smartico. Pick the platform's
   own tier ladder and it draws that: a different quantity wearing the same
   shape, so it drops the "XP" and keeps the crests. A site with no working
   Smartico label draws tiers whatever is picked, because the other option
   cannot answer.

   The mission has no second source and does not invent one. Smartico or
   nothing, the way the whitelabel's own mission widget works — a brand that
   runs no missions shows no mission widget, rather than an admin-typed goal
   standing in for a programme it does not have. The app-owned goals still have
   their page at /missions.

   docs/findings/missions-and-xp-are-two-progressions-in-the-kit-and-one-in-the.md */

function RankWidget() {
  const { isSignedIn } = useIdentity();
  const { isEnabled, api } = useSmartico();
  const { levelSource } = useContent().smartico;
  const { level: smarticoLevel, isPending: smarticoPending } = useSmarticoLevel();
  /* One source or the other, never both. Which one is the operator's stated
     choice, not an inference — the whitelabel's `levelComponent.dataSource`.
     Smartico owns the widget outright when it is picked: falling back to tier
     progress while it loads would flash a different quantity through the same
     bar, and falling back when it has nothing to say would put two ladders on
     one screen. */
  const usesSmartico =
    levelSourceFor({ setting: levelSource, smarticoEnabled: isEnabled }) === "smartico";
  const bankrolls = useBankrolls(isSignedIn && !usesSmartico);
  const tierProgress = useTierProgress(bankrolls.data?.at(0)?.id, isSignedIn && !usesSmartico);

  if (!isSignedIn) return null;

  /* No API object means the vendor script never started — most often an origin
     the Smartico account has not been told about. Render nothing rather than a
     skeleton that resolves on nothing, which is what a brand without Smartico
     used to sit under forever. */
  if (usesSmartico && api === null) return null;

  if (usesSmartico && smarticoPending) {
    return (
      <WidgetShell className="flex items-center justify-center px-[16px] py-[12px]">
        <RailWidgetSkeleton className="h-[30px] w-[224px]" />
      </WidgetShell>
    );
  }

  const level = usesSmartico ? smarticoLevel : tierLevelView(tierProgress.data ?? null);

  /* Nothing to show a position on. An empty bar would imply a programme the
     player is at the bottom of. */
  if (!level) return null;

  const open = usesSmartico ? () => openSmartico(LEVELS_DEEP_LINK) : null;

  return (
    <WidgetShell
      className="flex items-center justify-center px-[16px] py-[12px]"
      onClick={open}
      label={`${level.spoken}.${open ? " Open the level dashboard" : ""}`}
    >
      {/* Three hovers, as the whitelabel's SidebarLevelWidget draws them: the
          crests name their level, the bar states the pair in full and what is
          left to the next one. The crest images carry no text of their own,
          so without these the widget never says which tier it is showing. */}
      <div className="flex w-[224px] items-start gap-[4px]">
        {/* A tier progression cannot change carries a lock on its crest and a
            second line on the hover, as the whitelabel's widget draws it; the
            bar stays, since the figures still mean what they say. */}
        <Tooltip
          content={
            level.locked ? (
              <span className="flex flex-col gap-[2px]">
                <span>{level.name}</span>
                <span className="text-fg-subtle">Tier locked</span>
              </span>
            ) : (
              level.name
            )
          }
        >
          <LevelCrest src={level.imageUrl} alt={level.name} locked={level.locked} />
        </Tooltip>
        <Tooltip
          className="min-w-0 flex-1"
          content={
            <span className="flex flex-col gap-[2px]">
              <span>{level.exact}</span>
              {level.toNext && <span className="text-fg-subtle">{level.toNext}</span>}
            </span>
          }
        >
          <ProgressBar
            value={Math.round(level.percent)}
            height={30}
            chevron={open !== null}
            label={level.label}
            className="flex-1"
          />
        </Tooltip>
        <Tooltip content={level.nextName ?? "Top of the ladder"}>
          <LevelCrest src={level.nextImageUrl} alt={level.nextName ?? "Next level"} />
        </Tooltip>
      </div>
    </WidgetShell>
  );
}

/* The crest for one level or tier. Both sources publish real artwork —
   `imageUrl` on a platform tier, `image` on a Smartico level — which is why
   there is no drawn fallback here: a generic ring that never changes says the
   player is not progressing, which is the one thing this widget exists to deny.
   With no image the slot is an empty well.
   docs/findings/every-icon-badge-and-mark-is-hand-authored-svg-rather-than-the-kit-s.md */
function LevelCrest({ src, alt, locked = false }: { src: string | null; alt: string; locked?: boolean }) {
  const crest = (
    <span className="relative grid h-[30px] w-[30px] shrink-0 place-items-center overflow-hidden rounded-full bg-elevation-3">
      {src && <RemoteImage src={src} alt={alt} width={30} height={30} />}
    </span>
  );

  if (!locked) return crest;

  /* The badge sits outside the crest's clip, on its bottom-right corner. */
  return (
    <span className="relative flex shrink-0">
      {crest}
      <span
        role="img"
        aria-label="Tier locked"
        className="absolute -bottom-[2px] -right-[2px] grid h-[16px] w-[16px] place-items-center rounded-full border border-border bg-elevation-3 text-fg"
      >
        <Icon name="lock" size={10} />
      </span>
    </span>
  );
}

/* Missions are Smartico's alone.

   The rail used to stand an admin-typed goal in when Smartico was off, which
   put a mission widget on a brand that runs no missions at all. The whitelabel
   draws this from the vendor or not at all — `SmarticoMissionWidget` returns
   null the moment there is no API — and the app-owned goals still have their
   own page at /missions, which is where an operator who wants them puts them.

   `api === null` covers the case a blank label does not: keys filled in for an
   origin the Smartico account has never been told about. The script loads, the
   API never appears, and this used to leave a skeleton pulsing forever. */
function MissionWidget() {
  const { isSignedIn } = useIdentity();
  const { isEnabled, api } = useSmartico();
  const { mission: smarticoMission, isPending: smarticoPending } = useSmarticoMission();

  if (!isSignedIn || !isEnabled || api === null) return null;

  if (smarticoPending) {
    return (
      <WidgetShell className="flex h-[92px] items-start justify-center px-[16px] py-[12px]">
        <RailWidgetSkeleton className="h-full w-[224px]" />
      </WidgetShell>
    );
  }

  return smarticoMission ? <SmarticoMissionRow mission={smarticoMission} /> : null;
}

function SmarticoMissionRow({ mission }: { mission: MissionView }) {
  const countdown = useCountdown(mission.deadlineMs);

  return (
    <WidgetShell
      className="flex h-[92px] items-start justify-center px-[16px] py-[12px]"
      onClick={() => openSmartico(MISSIONS_DEEP_LINK)}
      label={`${mission.name} mission — ${mission.countLabel ?? `${mission.percent}%`} complete. Open missions`}
    >
      <div className="flex w-[224px] flex-col items-start gap-[4px]">
        <MissionHeading title={mission.name} tag={countdown?.label} urgent={countdown?.isUrgent} />
        <div className="flex w-full items-start gap-[4px]">
          <LevelCrest src={mission.imageUrl || null} alt={mission.name} />
          <ProgressBar
            value={mission.percent}
            height={30}
            chevron
            label={mission.countLabel ?? `${mission.percent}%`}
            className="flex-1"
          />
        </div>
      </div>
    </WidgetShell>
  );
}

function MissionHeading({
  title,
  tag,
  urgent,
}: {
  title: string;
  tag?: string;
  urgent?: boolean;
}) {
  return (
    <div className="flex w-full flex-col items-start">
      <div className="flex h-[16px] w-full items-center gap-[2px]">
        <span className="flex flex-1 items-center gap-[2px] text-[9px] font-normal leading-[1.5] text-fg-subtle">
          <Icon name="target" size={12} />
          Mission
        </span>
        {tag && (
          <span
            className={cn(
              "flex h-[16px] max-w-[120px] shrink-0 items-center justify-center truncate rounded-sm px-[3px] text-[9px] font-bold leading-[1.5]",
              urgent ? "bg-danger/20 text-danger" : "bg-elevation-3 text-fg",
            )}
          >
            {tag}
          </span>
        )}
      </div>
      <p className="w-full truncate text-left text-[12px] font-bold leading-[1.6] text-fg">{title}</p>
    </div>
  );
}

/* A widget is a button when it has somewhere to go and a plain block when it
   does not — never a block wearing a button's chevron. The whole row is the
   target, the way the kit and the whitelabel both draw it. */
function WidgetShell({
  className,
  href,
  onClick,
  label,
  children,
}: {
  className: string;
  href?: string;
  onClick?: (() => void) | null;
  label?: string;
  children: ReactNode;
}) {
  const shell = cn("w-full border-b border-border", className);
  const interactive = cn(shell, "transition-colors hover:bg-elevation-2");

  /* A page of ours is a link, so it prefetches and middle-clicks like every
     other destination on the site. Smartico's dashboard is not a page — it is a
     deep link into their overlay — so that one is a button. Neither is a div
     with a chevron on it. */
  if (href) {
    return (
      <Link href={href} aria-label={label} className={interactive}>
        {children}
      </Link>
    );
  }

  if (!onClick) {
    return <div className={shell}>{children}</div>;
  }

  return (
    <button type="button" onClick={onClick} aria-label={label} className={interactive}>
      {children}
    </button>
  );
}

/* Redraws the tag as the deadline approaches, and only as often as the tag can
   actually change — once an hour while days are showing, once a minute in the
   last hour, never above a day. */
function useCountdown(deadlineMs: number | null) {
  const [now, setNow] = useState(0);

  useEffect(() => {
    if (deadlineMs === null) return;

    setNow(Date.now());

    const every = countdownTickMs(deadlineMs, Date.now());

    if (every === null) return;

    const timer = window.setInterval(() => setNow(Date.now()), every);

    return () => window.clearInterval(timer);
  }, [deadlineMs]);

  if (deadlineMs === null) return null;

  return countdownTag(deadlineMs, now === 0 ? Date.now() : now);
}

function NavList({
  label,
  heading,
  items,
  pathname,
  onNavigate,
  bordered = true,
  sectioned,
}: {
  label: string;
  heading?: string;
  items: Item[];
  pathname: string;
  onNavigate?: () => void;
  bordered?: boolean;
  sectioned?: boolean;
}) {
  if (sectioned) {
    return (
      <nav aria-label={label} className={cn("flex flex-col py-[8px]", bordered && "border-b border-border")}>
        {heading && (
          <p className="px-[16px] py-[8px] text-[9px] font-medium uppercase leading-[1.5] text-fg-subtle">{heading}</p>
        )}
        {items.map((item) => (
          <SectionItem key={item.label} item={item} active={pathname === item.href} onNavigate={onNavigate} />
        ))}
      </nav>
    );
  }

  return (
    <nav
      aria-label={label}
      className={cn("flex flex-col items-start px-[16px] py-[8px]", bordered && "border-b border-border")}
    >
      {items.map((item) => (
        <NavItem key={item.label} item={item} active={pathname === item.href} onNavigate={onNavigate} />
      ))}
    </nav>
  );
}

function NavItem({
  item,
  active,
  grouped,
  last,
  onNavigate,
}: {
  item: Item;
  active: boolean;
  grouped?: boolean;
  last?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className={cn(
        "group relative flex h-[40px] min-h-[40px] w-full items-center gap-[8px] py-[8px] pl-[8px] pr-[12px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
        grouped ? "bg-elevation-2" : "rounded-lg",
        grouped && last && "rounded-b-lg",
        active ? "text-fg" : "text-fg-subtle",
        active && (grouped ? "bg-elevation-3" : "bg-elevation-2"),
        "hover:bg-elevation-3 hover:text-fg",
      )}
    >
      {grouped && active && <span className="absolute inset-y-0 left-0 w-[3px] bg-brand" />}
      {item.iconUrl ? (
        /* The category's own art. Rounded and clipped to the icon's footprint so
           a brand logo sits where a glyph would, not as a raw rectangle. */
        <span className="relative grid h-[20px] w-[20px] shrink-0 place-items-center overflow-hidden rounded-[5px] bg-elevation-3">
          <RemoteImage src={item.iconUrl} alt="" width={20} height={20} />
        </span>
      ) : (
        <Icon name={item.icon} size={20} className="shrink-0" />
      )}
      <span className="flex-1 truncate text-left">{item.label}</span>
      {item.badge && <Badge>{item.badge}</Badge>}
    </Link>
  );
}

/* A link in the kit's non-grouped sidebar: a 36px row whose pill fills on
   hover and when current, and a short brand bar with a glow on the rail's
   own left edge marking the page you are on. */
function SectionItem({
  item,
  active,
  onNavigate,
}: {
  item: Item;
  active: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      href={item.href}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      className="group relative flex h-[36px] min-h-[36px] w-full items-center px-[16px]"
    >
      {active && (
        <span
          aria-hidden
          className="absolute left-0 top-[10px] h-[16px] w-[3px] rounded-r-rounded bg-brand-bold shadow-[-5px_0_15px_5px_var(--color-brand)]"
        />
      )}
      <span
        data-pill=""
        className={cn(
          "flex min-w-0 flex-1 items-center gap-[8px] rounded-lg p-[8px] text-[12px] font-bold leading-[1.6] transition-colors duration-200",
          active ? "bg-elevation-2 text-fg" : "text-fg-subtle group-hover:bg-elevation-2 group-hover:text-fg",
        )}
      >
        {item.iconUrl ? (
          <span className="relative grid h-[20px] w-[20px] shrink-0 place-items-center overflow-hidden rounded-[5px] bg-elevation-3">
            <RemoteImage src={item.iconUrl} alt="" width={20} height={20} />
          </span>
        ) : (
          <Icon name={item.icon} size={20} className={cn("shrink-0", active && "text-brand-bold")} />
        )}
        <span className="flex-1 truncate text-left">{item.label}</span>
        {item.badge && <Badge>{item.badge}</Badge>}
      </span>
    </Link>
  );
}

function NavGroup({
  label,
  icon,
  items,
  pathname,
  defaultOpen,
  onNavigate,
}: {
  label: string;
  icon: IconName;
  items: Item[];
  pathname: string;
  defaultOpen?: boolean;
  onNavigate?: () => void;
}) {
  const [open, setOpen] = useState(Boolean(defaultOpen));
  return (
    <div className="w-full">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={cn(
          "flex h-[40px] min-h-[40px] w-full items-center gap-[8px] py-[8px] pl-[8px] pr-[12px] text-[12px] font-bold leading-[1.6] text-fg transition-colors duration-200",
          open ? "rounded-t-lg bg-elevation-3" : "rounded-lg hover:bg-elevation-3",
        )}
      >
        <Icon name={icon} size={20} className="shrink-0" />
        <span className="flex-1 text-left">{label}</span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={spring}
          className="grid h-[20px] w-[20px] shrink-0 place-items-center"
        >
          <Icon name="chevron-down" size={18} />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="flex flex-col">
              {items.map((item, i) => (
                <NavItem
                  key={item.label}
                  item={item}
                  active={pathname === item.href}
                  grouped
                  last={i === items.length - 1}
                  onNavigate={onNavigate}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
